import Device from '@/util/Device'
import { UAParser } from 'ua-parser-js'
import dayjs from 'dayjs'
import duration from 'dayjs/plugin/duration'

// Enable dayjs duration plugin
dayjs.extend(duration)

/**
 * Strips HTML tags from a given string.
 *
 * @param {string} htmlString - The string containing HTML tags.
 * @returns {string} - The plain text without HTML tags.
 */
export function stripHtmlTags(htmlString) {
  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlString, 'text/html');
  return doc.body.textContent || "";
}


/**
 * truncate a number without rounding it
 *
 * @param {number} number - The number to truncate
 * @param {number} decimalPlaces - Decimals places to keep in the given number
 * @returns {number} - The truncated number
 */
export function truncateNumber(number, decimalPlaces) {
  const factor = Math.pow(10, decimalPlaces);
  return Math.floor(number * factor) / factor;
}

/**
 * Round a number to a specified number of decimal places with different rounding modes
 *
 * @param {number} number - The number to round
 * @param {number} decimalPlaces - Number of decimal places to round to (default: 2)
 * @param {string} mode - Rounding mode: 'round' (default), 'floor', 'ceil'
 * @returns {number} - The rounded number
 */
export function roundToDecimalPlaces(number, decimalPlaces = 2, mode = 'round') {
  const factor = Math.pow(10, decimalPlaces);
  
  switch (mode) {
    case 'floor':
      return Math.floor(number * factor) / factor;
    case 'ceil':
      return Math.ceil(number * factor) / factor;
    case 'round':
    default:
      return Math.round(number * factor) / factor;
  }
}


/**
 * Removes all spaces from a given string.
 *
 * @param {string} str - The input string.
 * @returns {string} - The string without any spaces.
 */
export function removeSpaces(str) {
  return str.replace(/\s+/g, '');
}

/**
 * Removes all line breaks from a given string.
 *
 * @param {string} str - The input string.
 * @returns {string} - The string without any line breaks.
 */
export function removeLineBreaks(str) {
  return str.replace("\\n", '');
}

export function isOnStaging()
{
  let app_front_url = import.meta.env.VITE_FRONT_URL

  if (!app_front_url) return false

  let app_front_split = app_front_url.split('.')

  return app_front_split.filter((str) => str === 'https://staging').length > 0
}

/** True when the build talks to the production exchange (`VITE_URL`), whatever the front-end host. */
export function isProductionApi() {
  try {
    return new URL(import.meta.env.VITE_URL).hostname === 'api.futuur.com'
  } catch (_) {
    return false
  }
}

// =================================================================
// SESSION & PERFORMANCE TRACKING HELPERS
// =================================================================

// Session tracking variables
let sessionStartTime = Date.now()
let pageStartTime = Date.now()
let lastInteractionTime = Date.now()
let previousRoute = '/'
let sessionErrorCount = 0

/**
 * Gets the session start time
 * @returns {number} Session start timestamp
 */
export function getSessionStartTime() {
  return sessionStartTime
}

/**
 * Gets the current page start time
 * @returns {number} Page start timestamp
 */
export function getPageStartTime() {
  return pageStartTime
}

/**
 * Gets the last user interaction time
 * @returns {number} Last interaction timestamp
 */
export function getLastInteractionTime() {
  return lastInteractionTime
}

/**
 * Gets the previous route
 * @returns {string} Previous route path
 */
export function getPreviousRoute() {
  return previousRoute
}

/**
 * Gets the current session error count
 * @returns {number} Number of errors this session
 */
export function getSessionErrorCount() {
  return sessionErrorCount
}

/**
 * Updates route tracking when navigation occurs
 * @param {string} newRoute - The new route path
 */
export function updateRouteTracking(newRoute) {
  if (typeof window !== 'undefined') previousRoute = window.location.pathname
  pageStartTime = Date.now()
}

/**
 * Updates the last interaction timestamp
 */
export function updateLastInteraction() {
  lastInteractionTime = Date.now()
}

/**
 * Increments the session error count
 */
export function incrementErrorCount() {
  sessionErrorCount++
}

/**
 * Resets session tracking (call on new session)
 */
export function resetSessionTracking() {
  sessionStartTime = Date.now()
  pageStartTime = Date.now()
  lastInteractionTime = Date.now()
  previousRoute = '/'
  sessionErrorCount = 0
}

/**
 * Gets page load time using Performance API
 * @returns {number|null} Page load time in milliseconds
 */
export function getPageLoadTime() {
  if (performance.timing) {
    return performance.timing.loadEventEnd - performance.timing.navigationStart
  }
  return null
}

/**
 * Gets current session duration
 * @returns {number} Session duration in milliseconds
 */
export function getSessionDuration() {
  return Date.now() - sessionStartTime
}

/**
 * Gets time spent on current page
 * @returns {number} Time on page in milliseconds
 */
export function getTimeOnCurrentPage() {
  return Date.now() - pageStartTime
}

/**
 * Gets time since last user interaction
 * @returns {number} Time since last interaction in milliseconds
 */
export function getTimeSinceLastInteraction() {
  return Date.now() - lastInteractionTime
}

/**
 * Gets memory usage information
 * @returns {object|null} Memory usage data in MB
 */
export function getMemoryUsage() {
  if (performance.memory) {
    return {
      used_mb: Math.round(performance.memory.usedJSHeapSize / 1048576),
      total_mb: Math.round(performance.memory.totalJSHeapSize / 1048576),
      limit_mb: Math.round(performance.memory.jsHeapSizeLimit / 1048576)
    }
  }
  return null
}

/**
 * Gets user timezone
 * @returns {string} User's timezone
 */
export function getUserTimezone() {
  return Intl.DateTimeFormat().resolvedOptions().timeZone
}

/**
 * Gets viewport dimensions
 * @returns {string} Viewport size as "widthxheight"
 */
export function getViewportSize() {
  if (typeof window === 'undefined') return '0x0'
  return `${window.innerWidth}x${window.innerHeight}`
}

/**
 * Gets screen resolution
 * @returns {string} Screen resolution as "widthxheight"
 */
export function getScreenResolution() {
  if (typeof window === 'undefined' || !window.screen) return '0x0'
  return `${window.screen.width}x${window.screen.height}`
}

/**
 * Parses user agent to extract browser name and version
 * @returns {object} Browser information
 */
export function getBrowserInfo() {
  if (typeof navigator === 'undefined') return { browserName: 'Unknown', browserVersion: 'Unknown' }
  const userAgent = navigator.userAgent
  let browserName = 'Unknown'
  let browserVersion = 'Unknown'

  // Chrome
  if (userAgent.includes('Chrome') && !userAgent.includes('Edg')) {
    browserName = 'Chrome'
    const match = userAgent.match(/Chrome\/([0-9.]+)/)
    browserVersion = match ? match[1] : 'Unknown'
  }
  // Safari (must check before Chrome since Safari also contains Chrome in user agent)
  else if (userAgent.includes('Safari') && !userAgent.includes('Chrome')) {
    browserName = 'Safari'
    const match = userAgent.match(/Version\/([0-9.]+)/)
    browserVersion = match ? match[1] : 'Unknown'
  }
  // Firefox
  else if (userAgent.includes('Firefox')) {
    browserName = 'Firefox'
    const match = userAgent.match(/Firefox\/([0-9.]+)/)
    browserVersion = match ? match[1] : 'Unknown'
  }
  // Edge
  else if (userAgent.includes('Edg')) {
    browserName = 'Edge'
    const match = userAgent.match(/Edg\/([0-9.]+)/)
    browserVersion = match ? match[1] : 'Unknown'
  }
  // Opera
  else if (userAgent.includes('OPR')) {
    browserName = 'Opera'
    const match = userAgent.match(/OPR\/([0-9.]+)/)
    browserVersion = match ? match[1] : 'Unknown'
  }
  // Internet Explorer
  else if (userAgent.includes('Trident')) {
    browserName = 'Internet Explorer'
    const match = userAgent.match(/rv:([0-9.]+)/)
    browserVersion = match ? match[1] : 'Unknown'
  }

  return {
    name: browserName,
    version: browserVersion,
    full_version: `${browserName} ${browserVersion}`
  }
}

/**
 * Gets comprehensive device information using UAParser
 * @returns {object} Detailed device information
 */
export function getDeviceInfo() {
  const parser = new UAParser()
  const browserInfo = parser.getBrowser()
  const deviceInfo = parser.getDevice()
  const osInfo = parser.getOS()
  const engineInfo = parser.getEngine()
  const cpuInfo = parser.getCPU()

  // Determine device category
  let deviceCategory = 'unknown'
  let deviceType = 'unknown'
  let osFamily = 'unknown'

  // Device categorization
  if (deviceInfo.type === 'mobile') {
    deviceCategory = 'mobile'
    deviceType = 'mobile_phone'
  } else if (deviceInfo.type === 'tablet') {
    deviceCategory = 'tablet'
    deviceType = 'tablet'
  } else if (deviceInfo.type === 'console') {
    deviceCategory = 'console'
    deviceType = 'gaming_console'
  } else if (deviceInfo.type === 'smarttv') {
    deviceCategory = 'tv'
    deviceType = 'smart_tv'
  } else if (deviceInfo.type === 'wearable') {
    deviceCategory = 'wearable'
    deviceType = 'wearable'
  } else {
    // Default to desktop/computer if no specific type detected
    deviceCategory = 'desktop'
    deviceType = 'computer'
  }

  // OS Family categorization
  if (osInfo.name) {
    if (osInfo.name === 'iOS') {
      osFamily = 'ios'
    } else if (osInfo.name === 'Android') {
      osFamily = 'android'
    } else if (osInfo.name === 'Windows') {
      osFamily = 'windows'
    } else if (osInfo.name === 'Mac OS') {
      osFamily = 'macos'
    } else if (osInfo.name === 'Linux') {
      osFamily = 'linux'
    } else if (osInfo.name === 'Chrome OS') {
      osFamily = 'chromeos'
    } else {
      osFamily = osInfo.name.toLowerCase().replace(/\s+/g, '_')
    }
  }

  return {
    // Device category (high-level)
    device_category: deviceCategory,
    device_type: deviceType,
    
    // Operating System
    os_name: osInfo.name || 'unknown',
    os_version: osInfo.version || 'unknown',
    os_family: osFamily,
    
    // Device details
    device_vendor: deviceInfo.vendor || null,
    device_model: deviceInfo.model || null,
    
    // Browser details
    browser_name: browserInfo.name || 'unknown',
    browser_version: browserInfo.version || 'unknown',
    browser_engine: engineInfo.name || null,
    browser_engine_version: engineInfo.version || null,
    
    // CPU/Architecture
    cpu_architecture: cpuInfo.architecture || null,
    
    // Platform environment
    is_mobile_device: Device.isMobile,
    is_tablet: deviceInfo.type === 'tablet',
    is_desktop: deviceCategory === 'desktop',
    is_ios: Device.isIOS,
    is_android: Device.isAndroid,
    is_cordova: false,
    is_webapp_mobile: false,
    is_mobile_viewport: Device.isMobileSize,
    
    // Browser-specific flags
    is_chrome: Device.isChrome,
    is_safari: Device.isSafari,
    has_safe_area: Device.hasSafeArea,
    
    // Raw user agent for debugging
    user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
  }
}

/**
 * Gets user's IP address using a public API
 * @returns {Promise<string|null>} User's IP address
 */
export async function getUserIPAddress() {
  try {
    const response = await fetch('https://api.ipify.org?format=json', {
      timeout: 5000
    })
    const data = await response.json()
    return data.ip
  } catch (error) {
    console.warn('Failed to get IP address:', error)
    return null
  }
}

/**
 * Gets user's location based on IP address
 * @param {string} ipAddress - User's IP address
 * @returns {Promise<object|null>} Location information
 */
export async function getLocationFromIP(ipAddress) {
  if (!ipAddress) return null
  
  try {
    const response = await fetch(`https://ipapi.co/${ipAddress}/json/`, {
      timeout: 5000
    })
    const data = await response.json()
    
    if (data.error) {
      return null
    }
    
    return {
      country: data.country_name,
      country_code: data.country_code,
      region: data.region,
      city: data.city,
      postal: data.postal,
      latitude: data.latitude,
      longitude: data.longitude,
      timezone: data.timezone,
      org: data.org // ISP/Organization
    }
  } catch (error) {
    console.warn('Failed to get location from IP:', error)
    return null
  }
}

/**
 * Gets user's location using browser geolocation API (requires user permission)
 * @returns {Promise<object|null>} Precise location information
 */
export function getBrowserLocation() {
  return new Promise((resolve) => {
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      resolve(null)
      return
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        resolve({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy,
          source: 'browser_geolocation'
        })
      },
      (error) => {
        console.warn('Geolocation error:', error.message)
        resolve(null)
      },
      {
        timeout: 10000,
        enableHighAccuracy: false,
        maximumAge: 300000 // 5 minutes
      }
    )
  })
}

/**
 * Gets comprehensive location data (combines IP and browser geolocation)
 * @returns {Promise<object|null>} Combined location information
 */
export async function getLocationData() {
  try {
    // Get IP address first
    const ipAddress = await getUserIPAddress()
    
    // Get location from IP (always works, no permission needed)
    const ipLocation = await getLocationFromIP(ipAddress)
    
    // Try to get more precise browser location (requires permission)
    const browserLocation = await getBrowserLocation()
    
    return {
      ip_address: ipAddress,
      ip_location: ipLocation,
      browser_location: browserLocation,
      // Provide fallback data structure
      country: ipLocation?.country || null,
      country_code: ipLocation?.country_code || null,
      region: ipLocation?.region || null,
      city: ipLocation?.city || null,
      timezone: ipLocation?.timezone || getUserTimezone(),
      latitude: browserLocation?.latitude || ipLocation?.latitude || null,
      longitude: browserLocation?.longitude || ipLocation?.longitude || null,
    }
  } catch (error) {
    console.warn('Failed to get location data:', error)
    return {
      ip_address: null,
      ip_location: null,
      browser_location: null,
      country: null,
      country_code: null,
      region: null,
      city: null,
      timezone: getUserTimezone(),
      latitude: null,
      longitude: null,
    }
  }
}

// =================================================================
// COMPREHENSIVE ANALYTICS DATA GATHERING
// =================================================================

/**
 * Formats milliseconds to human-readable time string using dayjs
 * @param {number} ms - Milliseconds to format
 * @returns {string} Formatted time string
 */
function formatMillisecondsToReadableTime(ms) {
  if (!ms || ms < 0) return null
  
  if (ms < 1000) {
    return `${Math.round(ms)}ms`
  }
  
  const dur = dayjs.duration(ms)
  
  if (dur.asDays() >= 1) {
    const days = Math.floor(dur.asDays())
    const hours = dur.hours()
    return hours > 0 ? `${days}d ${hours}h` : `${days}d`
  }
  
  if (dur.asHours() >= 1) {
    const hours = Math.floor(dur.asHours())
    const minutes = dur.minutes()
    return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`
  }
  
  if (dur.asMinutes() >= 1) {
    const minutes = Math.floor(dur.asMinutes())
    const seconds = dur.seconds()
    return seconds > 0 ? `${minutes}m ${seconds}s` : `${minutes}m`
  }
  
  return `${Math.floor(dur.asSeconds())}s`
}

/**
 * Gathers comprehensive analytics data automatically from Redux store
 * @param {object} additionalContext - Additional context-specific data
 * @param {boolean} includeLocation - Whether to include location data (makes function async)
 * @returns {Promise<object>|object} Complete analytics data object
 */
export async function gatherAnalyticsData(additionalContext = {}) {
  const memoryUsage = getMemoryUsage()
  const deviceInfo = getDeviceInfo()
  
  // Lazy-load Redux store + selectors to avoid circular module init (store -> account -> ... -> helpers -> store).
  const [{ default: store }, { selectCurrentUser }, { selectActiveCurrency }] = await Promise.all([
    import('@/store'),
    import('@/store/account'),
    import('@/store/app'),
  ])

  const state = store.getState()
  const currentUser = selectCurrentUser(state)
  const activeCurrencyId = selectActiveCurrency(state)
  
  const routeParams = typeof window !== 'undefined' ? window.location.search : ''

  // Mixpanel auto-tracks: $current_url, $browser, $browser_version, $os,
  // $screen_height, $screen_width, $city, $region, mp_country_code,
  // $device, user_agent, and Time — so those are omitted here.
  return {
    // === USER CONTEXT ===
    is_authenticated: !!currentUser?.id,
    user_id: currentUser?.id || null,
    username: currentUser?.username || null,
    user_timezone: getUserTimezone(),
    
    // === CURRENCY & MARKET ===
    active_currency: activeCurrencyId || null,
    currency_mode: activeCurrencyId === 'OOM' ? 'play_money' : 'real_money',
    
    // === APPLICATION STATE ===
    is_mobile_size: deviceInfo.is_mobile_viewport,
    previous_route: getPreviousRoute(),
    route_params: routeParams,
    
    // === TECHNICAL CONTEXT ===
    cpu_architecture: deviceInfo.cpu_architecture,
    connection_type: typeof navigator !== 'undefined' && navigator.connection?.effectiveType || null,
    is_online: typeof navigator !== 'undefined' ? navigator.onLine : true,
    memory_limit_mb: memoryUsage?.limit_mb || null,
    has_safe_area: deviceInfo.has_safe_area,
    
    // === SESSION CONTEXT ===
    total_session_time: formatMillisecondsToReadableTime(getSessionDuration()),
    page_loading_time: formatMillisecondsToReadableTime(getPageLoadTime()),
    time_spent_on_current_page: formatMillisecondsToReadableTime(getTimeOnCurrentPage()),
    device_memory_available: memoryUsage?.limit_mb ? `${memoryUsage.limit_mb}MB` : null,
    time_since_last_interaction: formatMillisecondsToReadableTime(getTimeSinceLastInteraction()),
    errors_in_this_session: getSessionErrorCount(),
    
    // === ADDITIONAL CONTEXT ===
    ...additionalContext
  }
}

/**
 * Check if an email is a Privy-generated email
 *
 * @param {string} email - The email to check
 * @returns {boolean} - True if the email contains 'privy.local', false otherwise
 */
export function isPrivyEmail(email) {
  return email && email.includes('privy.local')
}
